import { useTu } from "@src/appV2/i18n/useTu";
import { Alert, Stack, TextField } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";
import { Button } from "../../../components/Button";
import { Dialog } from "../../../components/Dialog";
import { LoadingButton } from "../../../components/LoadingButton";
import { Text } from "../../../components/Text";
import { useDeleteWorkerLock } from "../api/useDeleteWorkerLock";

interface UnlockConfirmationDialogProps {
  modalState: UseModalState;
  lockReason: string;
  unlockCondition: string;
  lockId: string;
  workplaceId: string;
}

export function UnlockConfirmationDialog(props: UnlockConfirmationDialogProps) {
  const { tu } = useTu("chat");

  const { modalState, lockReason, unlockCondition, lockId, workplaceId } = props;
  const [error, setError] = useState<string | null>(null);
  const deleteWorkerLock = useDeleteWorkerLock();

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setError(null);
    }
  }, [modalState.modalIsOpen]);

  const handleUnlock = async () => {
    setError(null);
    try {
      await deleteWorkerLock.mutateAsync({ workplaceId, lockId });
      modalState.closeModal();
    } catch {
      setError(tu("Failed to unlock this professional. Please try again."));
    }
  };

  return (
    <Dialog modalState={modalState} title={tu("Unlock Professional")}>
      <Stack gap={3}>
        <Stack gap={1}>
          <Text semibold>{tu("Reason for Locking")}</Text>
          <TextField multiline disabled value={lockReason} />
        </Stack>

        <Stack gap={1}>
          <Text semibold>{tu("How to Unlock")}</Text>
          <TextField multiline disabled value={unlockCondition} />
        </Stack>

        <Text>{tu("Are you sure you want to unlock this professional?")}</Text>

        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
      <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ marginTop: 4 }}>
        <Button variant="outlined" onClick={modalState.closeModal}>
          {tu("Cancel")}
        </Button>
        <LoadingButton
          isLoading={deleteWorkerLock.isLoading}
          variant="primary"
          onClick={handleUnlock}
        >
          {tu("Unlock")}
        </LoadingButton>
      </Stack>
    </Dialog>
  );
}
